import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    Home, Key, Users, Sparkles, Shirt, Search,
    ShieldAlert, ClipboardList, Sofa, Briefcase, Headphones, ChevronDown
} from 'lucide-react';

import PageBanner from '../layout/PageBanner';
import { ServiceBlog2 } from '../element/ServiceBlog';
import ServiceArea from '../element/ServiceArea';
import WithUs from '../components/WithUs';
import PriceList from '../components/PriceList';
import TestimonialBlog from '../components/TestimonialBlog';
import TouchStart from '../components/TouchStart';
import AccordionBlog from '../components/AccordionBlog';

interface ServiceData {
    icon: React.ReactNode;
    title: string;
    description: string;
    detailedDescription: string;
}

const ICON_PROPS = { size: 28, strokeWidth: 2 };

const Services = () => {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [selectedService, setSelectedService] = useState<ServiceData | null>(null);
    const [showAllServices, setShowAllServices] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isExpanding, setIsExpanding] = useState(false);

    // Detect mobile screen size
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleShowMoreServices = () => {
        setIsExpanding(true);
        setTimeout(() => {
            setShowAllServices(true);
        }, 300);
    };

    const serviceData: ServiceData[] = [
        {
            icon: <Home color="#d89d37" {...ICON_PROPS} />,
            title: t('servicesPage.propertyManagement'),
            description: t('servicesPage.propertyManagementShort'),
            detailedDescription: t('servicesPage.propertyManagementDetail')
        },
        {
            icon: <Key color="#5370e2" {...ICON_PROPS} />,
            title: t('servicesPage.keyHolding'),
            description: t('servicesPage.keyHoldingShort'),
            detailedDescription: t('servicesPage.keyHoldingDetail')
        },
        {
            icon: <Users color="#47b5b9" {...ICON_PROPS} />,
            title: t('servicesPage.meetAndGreet'),
            description: t('servicesPage.meetAndGreetShort'),
            detailedDescription: t('servicesPage.meetAndGreetDetail')
        },
        {
            icon: <Sparkles color="#d89d37" {...ICON_PROPS} />,
            title: t('servicesPage.cleaningService'),
            description: t('servicesPage.cleaningServiceShort'),
            detailedDescription: t('servicesPage.cleaningServiceDetail')
        },
        {
            icon: <Shirt color="#5370e2" {...ICON_PROPS} />,
            title: t('servicesPage.laundryService'),
            description: t('servicesPage.laundryServiceShort'),
            detailedDescription: t('servicesPage.laundryServiceDetail')
        },
        {
            icon: <Search color="#47b5b9" {...ICON_PROPS} />,
            title: t('servicesPage.propertyCheck'),
            description: t('servicesPage.propertyCheckShort'),
            detailedDescription: t('servicesPage.propertyCheckDetail')
        },
        {
            icon: <ShieldAlert color="#d89d37" {...ICON_PROPS} />,
            title: t('servicesPage.alarmResponse'),
            description: t('servicesPage.alarmResponseShort'),
            detailedDescription: t('servicesPage.alarmResponseDetail')
        },
        {
            icon: <ClipboardList color="#5370e2" {...ICON_PROPS} />,
            title: t('servicesPage.registerGuests'),
            description: t('servicesPage.registerGuestsShort'),
            detailedDescription: t('servicesPage.registerGuestsDetail')
        },
        {
            icon: <Sofa color="#47b5b9" {...ICON_PROPS} />,
            title: t('servicesPage.upholsteryCleaning'),
            description: t('servicesPage.upholderyCleaningShort'),
            detailedDescription: t('servicesPage.upholsteryCleaningDetail')
        },
        {
            icon: <Briefcase color="#d89d37" {...ICON_PROPS} />,
            title: t('servicesPage.mortgageServices'),
            description: t('servicesPage.mortgageServicesShort'),
            detailedDescription: t('servicesPage.mortgageServicesDetail')
        },
        {
            icon: <Headphones color="#5370e2" {...ICON_PROPS} />,
            title: t('servicesPage.support247'),
            description: t('servicesPage.support247Short'),
            detailedDescription: t('servicesPage.support247Detail')
        }
    ];

    const handleServiceClick = (service: ServiceData) => {
        setSelectedService(service);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedService(null);
    };

    const visibleServices = isMobile && !showAllServices ? serviceData.slice(0, 6) : serviceData;

    return (
        <>
            <PageBanner parentPage={t('servicesPage.parentPage')} activePage={t('servicesPage.activePage')} motherMenu={t('servicesPage.motherMenu')} />
            <section className="main-services-white page-services">
                <div className="container">
                    <div className="services-list">
                        <div className="row align-items-center">
                            {visibleServices.map((data, i) => (
                                <div className="col-xl-4 col-lg-4 col-md-6 col-6" key={i}>
                                    <ServiceBlog2
                                        icon={data.icon}
                                        title={data.title}
                                        description={data.description}
                                        onClick={() => handleServiceClick(data)}
                                    />
                                </div>
                            ))}
                        </div>
                        {isMobile && !showAllServices && (
                            <div className="row">
                                <div
                                    className={`col-12 show-more-btn-wrapper ${isExpanding ? 'hiding' : ''}`}
                                    style={{ textAlign: 'center', marginTop: '30px' }}
                                >
                                    <button
                                        onClick={handleShowMoreServices}
                                        className="sec-btn"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        <span>Show More</span>
                                        <ChevronDown size={18} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header closeButton className="service-modal-header">
                    <Modal.Title className="service-modal-title">
                        {selectedService?.title}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="service-modal-body">
                    {selectedService?.detailedDescription}
                </Modal.Body>
                <Modal.Footer className="service-modal-footer">
                    <button onClick={handleCloseModal} className="sec-btn service-modal-btn">
                        <span>{t('servicesPage.close')}</span>
                    </button>
                </Modal.Footer>
            </Modal>

            <section className="main-pricing-blue">
                <div className="container">
                    <PriceList />
                </div>
            </section>
            <section className="service-area-section">
                <ServiceArea />
            </section>
            <section className="main-with-us">
                <WithUs />
            </section>
            <section className="main-testimonial">
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <div className="center-title">
                                <span className="sub-title">{t('servicesPage.testimonialSubtitle')}</span>
                                <h2 className="h2-title">{t('servicesPage.testimonialTitle')}</h2>
                            </div>
                        </div>
                    </div>
                    <TestimonialBlog />
                </div>
            </section>
            <section className="main-contact page-inner-contact">
                <div className="container">
                    <TouchStart />
                </div>
            </section>

            {/* FAQ Section Start */}
            <section className="main-faq-content" style={{ background: '#ffffff' }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <div className="center-title">
                                <span className="sub-title">{t('home.faqSubtitle')}</span>
                                <h2 className="h2-title">{t('home.faqTitle')}</h2>
                            </div>
                            <div className="faq-accordion accordion-style">
                                <div className="row">
                                    <div className="col-lg-10 offset-lg-1">
                                        <AccordionBlog arrayData={[
                                            { title: t('faqPage.q1'), content: t('faqPage.a1') },
                                            { title: t('faqPage.q2'), content: t('faqPage.a2') },
                                            { title: t('faqPage.q3'), content: t('faqPage.a3') },
                                            { title: t('faqPage.q4'), content: t('faqPage.a4') },
                                            { title: t('faqPage.q5'), content: t('faqPage.a5') },
                                            { title: t('faqPage.q6'), content: t('faqPage.a6') },
                                        ]} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Services;